import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface ResendCodeProps {
  initialSeconds?: number
  onResend?: () => Promise<void>
}

export const ResendCode = ({
  initialSeconds = 60,
  onResend
}: ResendCodeProps) => {
  const { t } = useTranslation()
  const [seconds, setSeconds] = useState(initialSeconds)
  const [isActive, setIsActive] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let interval: NodeJS.Timeout

    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds(seconds => {
          if (seconds <= 1) {
            setIsActive(false)
            return 0
          }
          return seconds - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [isActive, seconds])

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
    const remainingSeconds = timeInSeconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleResend = async () => {
    if (isLoading) return

    try {
      setIsLoading(true)
      await onResend?.()
      setSeconds(initialSeconds)
      setIsActive(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {isActive ? (
        <motion.div
          key="countdown"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="text-zinc-500 text-sm"
        >
          {t('auth.resendCode.resendIn', { time: formatTime(seconds) })}
        </motion.div>
      ) : (
        <motion.button
          key="resend"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          onClick={handleResend}
          disabled={isLoading}
          className={`text-blue-600 text-sm ${isLoading ? 'opacity-50' : ''}`}
        >
          {isLoading ? t('auth.resendCode.sending') : t('auth.resendCode.sendCode')}
        </motion.button>
      )}
    </div>
  )
}

export default ResendCode